package com.karyadina.ui.screens.pomodorotimer.presentation

import androidx.compose.runtime.mutableStateOf
import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.karyadina.model.PomodoroConfig
import com.karyadina.model.SessionType
import com.karyadina.model.TimerState
import com.karyadina.service.PomodoroServiceImpl
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.isActive
import kotlinx.coroutines.launch

class PromodorTimerViewModel(
    private val service: PomodoroServiceImpl // Inject or pass the service
) : ViewModel() {
    private val _uiState = MutableStateFlow(PromodorTimerUiState())
    val uiState = _uiState.asStateFlow()

    private var timerJob: Job? = null
    private var currentSessionId: String? = null

    // Settings
    private var configFocusTime: Int = 25
    private var configShortBreakTime: Int = 5
    private var configLongBreakTime: Int = 15
    private var config: PomodoroConfig? = null
    private var updateConfig: PomodoroConfig? = null

    init {
        loadStats()
        // Init default focus type
        updateTimeForType(SessionType.FOCUS)
        
        // Observe storage refresh signals (e.g. from cloud sync)
        viewModelScope.launch {
            com.karyadina.service.AppStorage.refreshNotifier.collect {
                service.refreshFromStorage()
                loadStats()
                // Update display if IDLE
                if (_uiState.value.timerState == TimerState.IDLE) {
                    updateTimeForType(_uiState.value.selectedType)
                }
            }
        }
    }

    override fun onCleared() {
        super.onCleared()
        // Cancel any lingering timer jobs when ViewModel is destroyed
        timerJob?.cancel()
        timerJob = null
    }

    private fun updateTimeForType(focus: SessionType) {
         configFocusTime = service.getConfig().focusDuration * 60
         configShortBreakTime = service.getConfig().shortBreakDuration * 60
         configLongBreakTime = service.getConfig().longBreakDuration * 60

        // config
        config = service.getConfig()


        val durationSeconds = when (focus) {
            SessionType.FOCUS -> configFocusTime
            SessionType.SHORT_BREAK -> configShortBreakTime
            SessionType.LONG_BREAK -> configLongBreakTime
        }
        _uiState.update {
            it.copy(
                selectedType = focus,
                totalSeconds = durationSeconds,
                remainingSeconds = durationSeconds,
                progress = 1.0f,
                configFocusTime = configFocusTime,
                configShortBreakTime = configShortBreakTime,
                configLongBreakTime = configLongBreakTime,

                config = config
            )
        }
    }

    private fun loadStats() {
        viewModelScope.launch {
            val newStats = service.getStatistics()
            _uiState.update {
                it.copy(
                stats = newStats
            )
            }
        }
    }

    fun onEvent(event: PromodorTimerEvent) {
        when (event) {
            is PromodorTimerEvent.SelectSessionType -> {
                if (_uiState.value.timerState != TimerState.RUNNING
                    && _uiState.value.timerState != TimerState.PAUSED  ) {

                    updateTimeForType(event.type)
                }
            }
            // user typing
            is PromodorTimerEvent.UpdateTaskInput -> {
                _uiState.update {
                    it.copy(
                        taskInput = event.name
                    )
                }
            }
            is PromodorTimerEvent.StartSession -> startTimer()
            is PromodorTimerEvent.PauseSession -> pauseTimer()
            is PromodorTimerEvent.ResumeSession -> resumeTimer()
            is PromodorTimerEvent.ResumeSession -> resumeTimer()
            is PromodorTimerEvent.StopSession -> stopTimer()
            is PromodorTimerEvent.UpdateConfig -> {
                service.updateConfig(event.config)
                _uiState.update { 
                    it.copy(
                        config = event.config,
                        configFocusTime = event.config.focusDuration * 60,
                        configShortBreakTime = event.config.shortBreakDuration * 60,
                        configLongBreakTime = event.config.longBreakDuration * 60
                    ) 
                }
                
                // If timer is not running, update the display immediately
                if (_uiState.value.timerState == TimerState.IDLE || _uiState.value.timerState == TimerState.COMPLETED) {
                    updateTimeForType(_uiState.value.selectedType)
                }
            }
            else -> {}
        }
    }

    private fun startTickerLoop() {
        timerJob?.cancel()
        timerJob = viewModelScope.launch {
            while (isActive) {
                // Safety check: if we don't have an ID, stop
                val sessionId = currentSessionId ?: break

                val remaining = service.getRemainingTime(sessionId)

                _uiState.update { state ->
                    state.copy(
                        remainingSeconds = remaining,
                        progress = if (state.totalSeconds > 0) remaining.toFloat() / state.totalSeconds.toFloat() else 0f
                    )
                }

                if (remaining <= 0) {
                    // Verify this is still the active session before completing
                    if (currentSessionId == sessionId) {
                        handleSessionComplete(sessionId)
                    }
                    break
                }
                delay(1000) // wait 1 sec
            }
        }
    }

    private fun startTimer() {
        _uiState.update { it.copy(timerState = TimerState.RUNNING) }
        val taskName = _uiState.value.taskInput.ifBlank { null }
        val session = service.startSession(uiState.value.selectedType, taskName)

        currentSessionId = session.id
        _uiState.update {
            it.copy(
                activeTaskName = session.task
            )
        }
        startTickerLoop()

    }

    private fun pauseTimer() {
        timerJob?.cancel()
        currentSessionId?.let { service.pauseSession(it) }
        _uiState.update { it.copy(timerState = TimerState.PAUSED) }
    }

    private fun resumeTimer() {
        timerJob?.cancel()
        currentSessionId?.let { service.resumeSession(it) }
        _uiState.update { it.copy(timerState = TimerState.RUNNING) }
        startTickerLoop()
    }

    private fun stopTimer() {
        timerJob?.cancel()
        // service.stopSession(...)
        currentSessionId?.let { service.cancelSession(it) }
        currentSessionId = null
        _uiState.update { it.copy(timerState = TimerState.IDLE, activeTaskName = null) }
        // Reset time
        updateTimeForType(_uiState.value.selectedType)
    }

    private fun handleSessionComplete(sessionId: String) {
        // Safety: Only process if this is still the active session
        // Prevents stale ticker coroutines from interfering with new sessions
        if (currentSessionId != sessionId) {
            return
        }
        
        // 1. Complete session in backend
        service.completeSession(sessionId)
        currentSessionId = null
        
        // 2. Set State to IDLE immediately to allow transitions
        // Also fetch FRESH stats synchronously to ensure logic is correct
        val newStats = service.getStatistics()
        
        _uiState.update { 
            it.copy(
                timerState = TimerState.IDLE, 
                activeTaskName = null, 
                remainingSeconds = 0, 
                progress = 0f,
                stats = newStats // Update stats immediately in UI
            ) 
        }
        
        // 3. Determine next session type
        val stats = newStats
        // If we just finished a Focus, we go to Break.
        // If current streak is multiple of 4, Long Break. Else Short Break.
        val nextType = if (_uiState.value.selectedType == SessionType.FOCUS) {
             // Logic: currentStreak is the streak *after* the session we just finished.
             // If streak is 4, 8, 12... it's a Long Break.
            if (stats.currentStreak > 0 && stats.currentStreak % 4 == 0) SessionType.LONG_BREAK else SessionType.SHORT_BREAK
        } else {
            // If we finished a Break, next is Focus
            SessionType.FOCUS
        }
        
        // Switch to the next type (Time will update because state is IDLE)
        onEvent(PromodorTimerEvent.SelectSessionType(nextType))
        
        // 4. Check Auto-Start Logic (Safe fallback to defaults if config is null)
        val config = _uiState.value.config ?: service.getConfig()
        val shouldAutoStart = if (nextType == SessionType.FOCUS) {
             config.autoStartPomodoros
        } else {
             config.autoStartBreaks
        }
        
        if (shouldAutoStart) {
            startTimer()
        }
    }

}